import isLocalStorageAvailable from '@/helpers/isLocalStorageAvailable';

const UTM_PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content'
];

const CLICK_ID_PARAMS = [
    'gclid', // Google Ads
    'fbclid', // Meta Ads
    'ttclid', // TikTok Ads
    'msclkid', // Microsoft/Bing Ads
    'wbraid', // Google Enhanced Conversions (web-to-app)
    'gbraid', // Google Enhanced Conversions (view-through)
    'li_fat_id', // LinkedIn
    'dclid' // Display & Video 360
];

const EXPIRY_DAYS = 30; // align with most ad-platform attribution windows

function nowISO() {
    return new Date().toISOString();
}

function getStorageKey(accountId, suffix) {
    return `mk_${suffix}_${accountId}`;
}

function safeParse(raw) {
    if (!raw) return null;
    try {
        return JSON.parse(raw);
    } catch (_err) {
        return null;
    }
}

function isExpired(capturedAt) {
    return (
        Date.now() - new Date(capturedAt).getTime() >
        EXPIRY_DAYS * 24 * 60 * 60 * 1000
    );
}

/**
 * Extract attribution parameters from the current browser URL.
 * Returns `null` if neither UTM nor click-ID data is present.
 */
export function getAttributionFromUrl() {
    const referrer = document.referrer || '';
    let referrerHost = '';
    const siteHost = window.location.hostname;
    const hasReferrer = referrerHost && referrerHost !== siteHost;

    if (referrer) {
        try {
            const u = new URL(referrer);
            referrerHost = u.hostname.replace(/^www\\./, ''); // normalise
        } catch (_) {
            // leave as empty strings if URL parsing fails
        }
    }

    const urlParams = new URLSearchParams(window.location.search);

    const utmParameters = {};
    UTM_PARAMS.forEach(param => {
        const value = urlParams.get(param);
        if (value) utmParameters[param] = value;
    });

    const clickIds = {};
    CLICK_ID_PARAMS.forEach(param => {
        const value = urlParams.get(param);
        if (value) clickIds[param] = value;
    });

    if (
        !hasReferrer &&
        Object.keys(utmParameters).length === 0 &&
        Object.keys(clickIds).length === 0
    ) {
        return null; // no marketing data present
    }

    return {
        utmParameters,
        clickIds,
        referrer: document.referrer || '',
        referrerHost,
        landingUrl: window.location.origin + window.location.pathname,
        capturedAt: nowISO()
    };
}

/**
 * Load payload from localStorage (if present and not expired).
 */
export function loadAttribution(accountId, suffix) {
    if (!isLocalStorageAvailable()) return null;

    const raw = localStorage.getItem(getStorageKey(accountId, suffix));
    const data = safeParse(raw);

    if (data && !isExpired(data.capturedAt)) return data;

    localStorage.removeItem(getStorageKey(accountId, suffix));
    return null;
}

/**
 * Persist a payload to localStorage.
 * First-touch is write-once; last-touch is overwritten each call.
 */
export function saveAttribution(accountId, data, suffix) {
    if (!isLocalStorageAvailable()) return;

    const key = getStorageKey(accountId, suffix);
    if (suffix === 'ft' && localStorage.getItem(key)) return; // preserve FT

    localStorage.setItem(key, JSON.stringify(data));
}

export function clearAttributionStorage(accountId) {
    if (!isLocalStorageAvailable()) return;
    localStorage.removeItem(getStorageKey(accountId, 'ft'));
    localStorage.removeItem(getStorageKey(accountId, 'lt'));
}

const state = {
    firstTouch: null,
    lastTouch: null
};

const mutations = {
    SET_FIRST_TOUCH(state, payload) {
        state.firstTouch = payload;
    },
    SET_LAST_TOUCH(state, payload) {
        state.lastTouch = payload;
    },
    CLEAR_ATTRIBUTION(state) {
        state.firstTouch = null;
        state.lastTouch = null;
    }
};

const getters = {
    marketingDetails: state => state
};

const actions = {
    /**
     * Capture URL parameters + referrer and persist them.
     * Call only after the user has opted-in to marketing storage (GDPR).
     */
    captureMarketingDetails({ commit, rootState }, consent = true) {
        if (!consent) return;

        const accountId = rootState.venue?.venue?.accountId;
        if (!accountId) return;

        const cachedFT = loadAttribution(accountId, 'ft');
        const cachedLT = loadAttribution(accountId, 'lt');
        if (cachedFT) commit('SET_FIRST_TOUCH', cachedFT);
        if (cachedLT) commit('SET_LAST_TOUCH', cachedLT);

        const fresh = getAttributionFromUrl();
        if (!fresh) return;

        if (!cachedFT) {
            saveAttribution(accountId, fresh, 'ft');
            commit('SET_FIRST_TOUCH', fresh);
        }
        saveAttribution(accountId, fresh, 'lt');
        commit('SET_LAST_TOUCH', fresh);
    },

    /**
     * Wipe attribution from state and storage (e.g. on consent withdrawal).
     */
    clearMarketingDetails({ commit, rootState }) {
        commit('CLEAR_ATTRIBUTION');
        const accountId = rootState.venue?.venue?.accountId;
        if (accountId) clearAttributionStorage(accountId);
    }
};

export default {
    namespaced: true,
    state,
    mutations,
    getters,
    actions
};
